import { ref } from 'vue'
import type { GateDecision, GateRow } from '@/api'

/**
 * How long a decided GateDecisionCard takes to close up its space once it has faded (its styles hold the
 * same 250ms): a page showing those cards keeps a decided gate for MOTION.decisionMs plus this.
 */
export const GATE_CARD_CLOSE_MS = 250

/**
 * The gates you have just decided, as the pages that decide them show it. A decision marks the gate at once
 * (ZhDecisionCard's `decided`) and takes it off the page `showMs` later, without waiting on the API, so the
 * card has time to show the decision and fade. If the API refuses, the gate comes back as it was and
 * `decide` resolves false; it resolves true once the API has recorded it and the gate has gone.
 */
export function useGateDecisions(showMs: number) {
    /** The decision on each gate you have just decided, while it is still on the page. */
    const shown = ref<ReadonlyMap<string, GateDecision>>(new Map())
    /** The words of the choice each of those was decided through, for a gate that offered choices. */
    const labels = ref<ReadonlyMap<string, string>>(new Map())
    /** Gates decided and gone from the page, whatever a read still in flight says. */
    const gone = ref<ReadonlySet<string>>(new Set())

    /** The decision you have just made on a gate, if any. */
    function decidedOf(id: string): GateDecision | undefined {
        return shown.value.get(id)
    }

    /** The choice you have just decided a gate through, in its button's words, if it offered choices. */
    function decidedLabelOf(id: string): string | undefined {
        return labels.value.get(id)
    }

    /**
     * Whether something you decide belongs on the page: `waiting` on you, or showing the decision you just made
     * on it (a read that already says decided must not cut its fade short), and never once it has gone. Keyed
     * by the id `decide` was given, so it serves anything decided this way, not only a gate.
     */
    function stays(id: string, waiting: boolean): boolean {
        return !gone.value.has(id) && (waiting || shown.value.has(id))
    }

    /** Whether a gate belongs on the page: `stays`, with the gate's own status saying whether it waits. */
    function onPage(gate: GateRow): boolean {
        return stays(gate.id, gate.status === 'waiting')
    }

    async function decide(
        id: string,
        decision: GateDecision,
        record: () => Promise<unknown>,
        /** The words of the choice it was decided through, shown in place of Approved or Rejected. */
        label?: string,
    ): Promise<boolean> {
        shown.value = new Map([...shown.value, [id, decision]])
        if (label) {
            labels.value = new Map([...labels.value, [id, label]])
        }
        const leave = new Promise(resolve => setTimeout(resolve, showMs)).then(() => {
            if (shown.value.has(id)) {
                gone.value = new Set([...gone.value, id])
            }
        })
        try {
            await Promise.all([record(), leave])
            return true
        } catch {
            shown.value = new Map([...shown.value].filter(([gateId]) => gateId !== id))
            labels.value = new Map([...labels.value].filter(([gateId]) => gateId !== id))
            gone.value = new Set([...gone.value].filter(gateId => gateId !== id))
            return false
        }
    }

    return { decidedOf, decidedLabelOf, stays, onPage, decide }
}
